import type { ReactNode } from "react";
import { fallbackOf } from "../settings/catalog";
import { useFlag, useT, useText } from "../settings/context";
import { Band } from "../site/Band";
import { externalUrl } from "./links";
import { Icon, type IconName } from "../ui/Icon";
import { Prose } from "../ui/Prose";

/** A block's heading: what the organiser wrote, or what the catalog promises. */
function useHeading(key: string): string {
  return useText(key, fallbackOf(key));
}

/**
 * LE BAS DE L'ÉCRAN — LES COORDONNÉES D'UN CÔTÉ, CE QUI FERME LA PAGE DE L'AUTRE.
 *
 * ⚠️ DEUX COMPOSANTS PARCE QU'ILS NE VIVENT PAS SUR LES MÊMES ÉCRANS. `Footer` porte
 * les coordonnées de l'événement — organisateur, dates, lieu, contact — et ne se rend
 * que sur l'accueil : personne ne vient relire l'adresse du palais des congrès sous
 * vingt champs de formulaire. `FooterEnd`, lui, porte l'image et la ligne de mentions,
 * et ferme TOUS les écrans : une mention légale est due partout, et une page qui
 * s'arrête net sur son dernier champ n'a plus de bas. SUR QUELS ÉCRANS chacun est monté
 * est une question de registre, pas de pied de page — voir `hasFooter` dans
 * `routes/registry.ts`, que `Shell` lit pour ne poser que les coordonnées sur `/`.
 *
 * ⚠️ ET UN SEUL INTERRUPTEUR POUR LES DEUX, `FO_MEETING_V2_FOOTER_SHOW` : éteint, il
 * emporte les coordonnées, l'image et les mentions. Un interrupteur de plus, propre au
 * bas de page, aurait donné deux façons d'éteindre le même bout d'écran. Les deux
 * règles cohabitent : l'organisateur éteint son pied de page, le produit décide où il
 * vit.
 *
 * Two rules run through the whole thing: A BLOCK WITH NOTHING IN IT DOES NOT
 * APPEAR, and A CONTROL THAT CANNOT WORK IS NOT DRAWN. A heading with no answer
 * underneath, or a button that does nothing when pressed, both cost more than
 * the empty space they were meant to fill.
 *
 * Structure, from `chassis.css` and `desktop.css`:
 *   .foot__grid   organiser · dates · venue · contact
 *   .foot__act    the directions link, INSIDE the venue block (see `Footer`)
 *
 * ⚠️ IN EACH BLOCK, THE INFORMATION IS THE TEXT AND THE HEADING IS ITS CAPTION.
 * The first cut did the opposite — « Dates » in bold, the date in small grey under
 * it — and a client read the row as three lonely headings floating over a strip
 * of nothing. What a visitor comes to the footer for is the date, the venue, the
 * organiser's name: those are drawn in the text colour, at reading size; the
 * heading above them is a small tracked eyebrow, and the icon sits in a tinted
 * round chip that gives each block a visual anchor the bare 15px glyph never did.
 *
 * Displayed strings go through `t()`, French kept at the call site as the
 * fallback: what the site says of itself follows the language being read.
 */

function Item({
  icon,
  title,
  value,
  action,
}: {
  icon: IconName;
  title: string;
  value: string;
  /** Ce qu'on peut faire de l'information, posé sous elle (l'itinéraire du lieu). */
  action?: ReactNode;
}) {
  // No value, no block. The alternative is a heading with nothing under it,
  // which reads as a broken page rather than as an absent detail. An action
  // counts as content: a venue with only its map link still has a button to press.
  if (!value.trim() && !action) return null;
  /* `small` for the caption, `b` for the information: the tags follow the visual
     weight (see the header comment). The chip is an `i`, an inline element that
     carries no meaning of its own — the icon inside is `aria-hidden` already. */
  return (
    <div className="foot__item">
      <i className="foot__ico">
        <Icon name={icon} />
      </i>
      <span>
        <small>{title}</small>
        {value.trim() ? <b>{value}</b> : null}
        {action}
      </span>
    </div>
  );
}

export function Footer() {
  const t = useT();
  const visible = useFlag("FO_MEETING_V2_FOOTER_SHOW");
  const organiser = useText("FO_MEETING_V2_FOOTER_ORGANIZER");
  const dates = useText("FO_MEETING_V2_FOOTER_DATES");
  const place = useText("FO_MEETING_V2_EVENT_PLACE");
  const contact = useText("FO_MEETING_V2_FOOTER_CONTACT");
  const map = externalUrl(useText("FO_MEETING_V2_FOOTER_MAP_URL"));

  // Each block's heading is the organiser's to write — and to translate. The
  // values under them mostly are not: a company name, an address, an email.
  const organiserTitle = useHeading("FO_MEETING_V2_FOOTER_ORGANIZER_LABEL");
  const datesTitle = useHeading("FO_MEETING_V2_FOOTER_DATES_LABEL");
  const placeTitle = useHeading("FO_MEETING_V2_FOOTER_PLACE_LABEL");
  const contactTitle = useHeading("FO_MEETING_V2_FOOTER_CONTACT_LABEL");

  /* The organiser PASTES the venue's map address rather than us guessing it
     from the free-text venue: a link they opened themselves is one they know
     lands on the right door, and it works with a shortened share link or a
     precise entrance just as well.

     ⚠️ DANS LE BLOC DU LIEU, SOUS L'ADRESSE (tâche 551). Il vivait seul dans une
     rangée d'actions sous les quatre blocs, calé à droite sur bureau : loin de
     l'adresse qu'il sert, il se lisait comme un bouton du pied de page entier. Posé
     sous l'adresse, il dit de quoi il donne l'itinéraire.

     `noreferrer` as well as `noopener`: a map provider has no business being told
     which event page the visitor came from. */
  const directions = map ? (
    <a
      className="foot__act"
      href={map}
      target="_blank"
      rel="noopener noreferrer"
    >
      {t("DIRECTIONS", "Itinéraire")}
    </a>
  ) : null;

  return (
    <footer className="foot" hidden={!visible}>
      <div className="foot__grid" data-cfg="footerDetails">
        <Item icon="organiser" title={organiserTitle} value={organiser} />
        <Item icon="dates" title={datesTitle} value={dates} />
        <Item
          icon="place"
          title={placeTitle}
          value={place}
          action={directions}
        />
        <Item icon="contact" title={contactTitle} value={contact} />
      </div>
    </footer>
  );
}

/**
 * CE QUI FERME LA PAGE — l'image du bandeau, puis la ligne des mentions sous elle.
 *
 * ⚠️ SUR TOUS LES ÉCRANS, ET DONC HORS DU PIED DE PAGE. C'est ce qui l'a séparé de
 * `Footer` : les coordonnées sont une fiche d'identité qu'on va lire sur l'accueil,
 * l'image et les mentions sont l'ourlet de la page — il court sous le tunnel, les pages
 * du site et la confirmation comme sous la home.
 *
 * ⚠️ FRÈRES ET NON ENFANTS DU PIED DE PAGE, tous les deux pleine largeur, comme le
 * bandeau du haut : le pied de page ramène son contenu à 900px centrés, et nichés
 * dedans l'image et la mention seraient décalées avec les coordonnées au lieu de tenir
 * d'un bord à l'autre. Dehors, elles se posent toutes seules (`chassis.css`).
 *
 * ⚠️ ET L'ESPACE LIBRE D'UNE PAGE COURTE LUI REVIENT QUAND LES COORDONNÉES NE SONT PAS
 * LÀ. `margin-top: auto` est sur `.footend`, annulé par la règle voisine dès qu'un pied
 * de page visible le précède : sur l'accueil c'est le pied de page qui descend et
 * entraîne l'image avec lui, ailleurs c'est l'ourlet seul. Les deux `auto` à la fois se
 * partageraient l'espace et laisseraient le pied de page flotter au milieu de l'écran.
 *
 * ⚠️ ELLES S'ÉTEIGNENT AVEC LE PIED DE PAGE, `FO_MEETING_V2_FOOTER_SHOW` : le bas de
 * l'écran se règle d'un seul geste (voir l'en-tête du fichier).
 */
export function FooterEnd() {
  const visible = useFlag("FO_MEETING_V2_FOOTER_SHOW");
  const notice = useText("FO_MEETING_V2_FOOTER_NOTICE");

  return (
    <div className="footend">
      <Band
        imageKey="FO_MEETING_V2_FOOTER_IMAGE"
        focusKey="FO_MEETING_V2_FOOTER_IMAGE_FOCUS"
        safeKey="FO_MEETING_V2_FOOTER_IMAGE_SAFE"
        sidesKey="FO_MEETING_V2_FOOTER_IMAGE_SIDES"
        heightKey="FO_MEETING_V2_FOOTER_IMAGE_HEIGHT"
        cfg="footerImage"
        className="band--foot"
        off={!visible}
      />

      {/* ⚠️ LES MENTIONS RESTENT DANS LE DOM QUAND ELLES SONT VIDES, `hidden` plutôt que
          `null` : le panneau vise ses réglages par leur `data-cfg`, et une ancre retirée
          dès que le champ est vide ne se laisse plus survoler au moment même où
          l'organisateur vient l'écrire. */}
      <Prose
        html={noticeHtml(notice)}
        className="foot__legal"
        data-cfg="footerNotice"
        hidden={!visible || !notice.trim()}
      />
    </div>
  );
}

/**
 * LES MENTIONS, EN HTML, Y COMPRIS CELLES ÉCRITES QUAND C'ÉTAIT DU TEXTE.
 *
 * ⚠️ UNE VALEUR SANS BALISE EST L'ANCIEN TEXTE SIMPLE, et ses retours à la ligne
 * étaient rendus (`white-space: pre-line`). Lue telle quelle comme du HTML, elle se
 * replierait sur une seule ligne. Elle est donc échappée et ses retours à la ligne
 * deviennent des `<br>`. Dès que l'organisateur la réenregistre depuis l'éditeur, elle
 * porte ses balises et passe sans retouche.
 */
export function noticeHtml(value: string): string {
  if (/<[a-z!/]/i.test(value)) return value;
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\r?\n/g, "<br>");
}
